import { combineReducers } from "redux";
import { createMigrate, persistReducer } from "redux-persist";
import localforage from "localforage";

import analytics from "./ducks/analytics";
import auth from "./ducks/auth";
import commodities from "./ducks/commodities";
import datasets from "./ducks/datasets";
import date from "./ducks/date";
import incidentCausesDuck from "./ducks/incidentCauses";
import deliveryIssuesDuck from "./ducks/deliveryIssues";
import distributionSitesDuck from "./ducks/distributionSites";
import distributionReports from "./ducks/distributionReports";
import schoolsImports from "./ducks/schoolsImports";
import batchNumbers from "./ducks/batchNumbers";
import language from "./ducks/language";
import reportsDuck, {
  recalculateReducer as reportsRecalculateReducer,
} from "./ducks/reports";
import reportsV3Duck, {
  recalculateReducer as reportsV3RecalculateReducer,
} from "./ducks/reportsV3";
import schools from "./ducks/schools";
import foodSources from "./ducks/foodSources";
import noMealReasons from "./ducks/noMealReasons";
import featureFlags from "./ducks/featureFlags";
import initialization from "./ducks/initialization";
import reportSignatures from "./ducks/reportSignatures";
import storeDoctor from "./ducks/storeDoctor";
import stores from "./ducks/stores";
import users from "./ducks/users";
import localAuth from "./ducks/localAuth";
import stockInspect from "./ducks/stockInspect";
import stockSiblings from "./ducks/stockSiblings";
import { countriesDuck } from "./ducks/countries";
import { regionsDuck } from "./ducks/regions";
import migrations from "./migrations";
import schoolProfile from "./ducks/schoolProfile";
import toast from "./ducks/toast";
import authorityLevels from "./ducks/authorityLevels";
import adminLevels from "./ducks/adminLevels";
import ageGroups from "./ducks/ageGroups";
import countryProfile from "./ducks/countryProfile";
import deliveries from "./ducks/deliveries";
import roles from "./ducks/roles";
import links from "./ducks/links";
import statements from "./ducks/statements";
import vendors from "./ducks/vendors";
import purchaseOtherCosts from "./ducks/purchaseOtherCosts";
import mvRefreshLog from "./ducks/mvRefreshLog";
import batchHeal from "./ducks/batchHeal";
import xray from "./ducks/xray";
import needsReview from "./ducks/needsReview";

const rootReducer = combineReducers({
  analytics,
  auth,
  adminLevels,
  batchHeal,
  ageGroups,
  authorityLevels,
  "batch-numbers": batchNumbers,
  commodities: commodities._reducer,
  countries: countriesDuck._reducer,
  countryProfile,
  datasets,
  date,
  deliveries,
  "delivery-issues": deliveryIssuesDuck._reducer,
  distributionReports,
  "distribution-sites": distributionSitesDuck._reducer,
  featureFlags,
  foodSources,
  mvRefreshLog,
  noMealReasons,
  "incident-causes": incidentCausesDuck._reducer,
  initialization,
  language,
  links,
  localAuth,
  purchaseOtherCosts,
  regions: regionsDuck._reducer,
  reportSignatures,
  reports: reportsDuck._reducer,
  reportsRecalculate: reportsRecalculateReducer,
  reportsV3: reportsV3Duck._reducer,
  reportsV3Recalculate: reportsV3RecalculateReducer,
  roles,
  schoolProfile,
  schools,
  schoolsImports,
  statements,
  needsReview,
  storeDoctor,
  stores,
  stockInspect,
  stockSiblings,
  toast,
  users,
  vendors,
  xray,
});

export type RootState = ReturnType<typeof rootReducer>;

const persistedRootReducer = persistReducer(
  {
    /**
     * Blacklist all reducers which already use persistReducer, otherwise:
     * - their individual configs may not be respected, depending on rehydration order
     * - localStorage space is wasted by duplicate data
     */
    blacklist: [
      "analytics",
      "auth",
      "batch-numbers",
      "commodities",
      "countries",
      "country-schools",
      "datasets",
      "delivery-issues",
      "distribution-sites",
      "distributionReports",
      "featureFlags",
      "foodSources",
      "mvRefreshLog",
      "incident-causes",
      "links",
      "localAuth",
      "purchaseOtherCosts",
      "regions",
      "reports",
      "reportsRecalculate",
      "reportsV3",
      "reportsV3Recalculate",
      "roles",
      "schoolProfile",
      "schools",
      "needsReview",
      "stockInspect",
      "stockSiblings",
      "storeDoctor",
      "stores",
      "vendors",
      "batchHeal",
      "xray",
    ],
    key: "root",
    migrate: createMigrate(migrations, { debug: true }),
    storage: localforage,
    timeout: 500,
    version: Object.keys(migrations).length,
  },
  rootReducer
);

export default persistedRootReducer;
